<template lang="pug">
    section(
        role="tabpanel",
        class="_con _tabps",
        v-show="active",
        :aria-labelledby="_id",
        :aria-hidden="!active")
        slot
</template>

<script>
    /**
     * A pane within an [ib-tabs](#ibtabs) component.
     * The `default` slot is for pane content. The `title` slot is for cases where the
     * title includes icons or other non-text content; it is an alternative to the
     * `title` property.
     */
    export default {
        name: 'ib-tab',

        props: {
            /**
             * An identifier, unique within this `ib-tabs` view, that can be used to
             * get or set the active tab from there. If not specified, the index number
             * will be used.
             */
            name: [String,Number],
            /**
             * Text title for this tab; if the `title` slot is not in use, this will
             * be displayed; otherwise, it will only be used for the `title` attribute,
             * used for things like mouse hover and screen readers.
             */
            title: {
                type: String,
                required: true
            },
            /**
             * Semantic color class; applies to underbar with `underline` appearance or
             * text with `clear` appearance.
             */
            tone: String,
            /**
             * Alignment (`start` or `end`); if it differs from the bar alignment, this tab
             * will be separated from the others.
             */
            align: String,
            /**
             * If true, grey out this tab and make it inert.
             */
            disabled: Boolean,
            /**
             * The URL location associated with this tab, if any.
             */
            href: String,
            /**
             * The router path associated with this tab, if any.
             */
            route: [Object,String],
            /**
             * If a route is specified, this tab will become active if any of its child routes are
             * navigated to, unless this property is `true`.
             */
            exact: Boolean,
            /**
             * DOM `id` attribute.
             */
            id: String,
            /**
             * Value to display in a badge assigned to this tab.
             */
            badge: [ Boolean, Number, String ]
        },

        data() {
            return {
                active: false
            };
        },

        computed: {
            _id() {
                return this.id || '_tab' + this._uid;
            }
        },

        mounted() {
            this.sync();
        },

        beforeDestroy() {
            this.$nextTick(this.sync);
        },

        methods: {
            /** Notify container of a potential layout change */
            sync() {
                this.$parent.sync();
            },

            /** Notify this tab that it has become active */
            show() {
                this.active = true;
                this.$emit('show');
            },

            /** Notify this tab that it has become inactive */
            hide() {
                this.active = false;
                this.$emit('hide');
            }
        }
    }
</script>
